import type { ReactiveVar } from '@apollo/client';
import { makeVar } from '@apollo/client';
import type { ReactiveStorage } from './graphqlClientStorage';
import { graphqlClientStorage } from './graphqlClientStorage';
import { ReactiveState } from './ReactiveState';

/**
 * a reactive variable that always returns null
 * used to avoid running through expensive logic
 */
const nullVar = makeVar(null);

export class ReactiveMap<
  K extends string = string,
  V extends ReactiveStorage[S] = any,
  S extends keyof ReactiveStorage = undefined,
> {
  private esMap = new Map<K, ReactiveState<V>>();

  private storageKey: string;

  /**
   * Wrapper of an ES6 Map from keys K to ReactiveStates of type V with some
   * utility functions
   * @see ReactiveState
   * @param storageKey if provided, use localstorage with this key
   * ⚠️ if using `storageKey`, you MUST define the shape of what's stored in `graphqlClientStorage`
   * @param initializer if provided, intializes the map with an array of entries
   * [K, V] if no values are found in local state
   */
  constructor(storageKey?: S, initializer?: [K, V][]) {
    /** if there's an initializer, set it */
    if (initializer) {
      initializer.forEach(([key, val]) => this.init(key, val));
    }

    /** try to rehydrate from storage if there's a storageKey */
    if (storageKey) {
      this.storageKey = storageKey;
      const existingStorage = this.getStorage();
      const entriesToStore = Object.entries(existingStorage);

      if (entriesToStore.length > 0) {
        entriesToStore.forEach(([key, val]) => {
          this.init(key as K, val as V);
        });
      }
    }
  }

  [Symbol.iterator] = () => {
    const vals = Array.from(this.esMap.entries());
    let index = -1;

    return {
      next: () => {
        index += 1;
        return {
          value: [vals[index]?.[0], vals[index]?.[1]?.get()] as [K, V],
          done: !(index in vals),
        };
      },
    };
  };

  private getStorage() {
    if (this.storageKey) {
      return graphqlClientStorage.get(`reactive-map-${this.storageKey}`, {});
    }
    return null;
  }

  private setStorage(key: K, val: V) {
    if (this.storageKey) {
      return graphqlClientStorage.set(
        `reactive-map-${this.storageKey}`,
        (draft: any) => {
          (draft ?? {})[key] = val;
          return draft;
        }
      );
    }
    return null;
  }

  private deleteStorage(key: K) {
    if (this.storageKey) {
      return graphqlClientStorage.set(
        `reactive-map-${this.storageKey}`,
        (draft: any) => {
          delete draft[key];
          return draft;
        }
      );
    }
    return null;
  }

  private init(key: K, value: V): V {
    const state = new ReactiveState(value);
    this.esMap.set(key, state);
    this.setStorage(key, state.get());
    return value;
  }

  /**
   * set a new state for a key
   * @param key map key
   * @param value ReactiveState value
   * @returns the value
   */
  set(key: K, value: V): Readonly<V>;

  /**
   * set a new state for a key
   * @param key map key
   * @param immer immer produce function which returns a new state
   * @see immer docs
   * @returns the value
   */
  set(key: K, immer: (draft: V) => V): Readonly<V>;

  /**
   * set a new state for a key
   * @param key map key
   * @param immer immer produce function which mutates the state
   * @see immer docs
   * @returns the value
   */
  set(key: K, immer: (draft: V) => void): Readonly<V>;

  set(key: K, value: V | ((draft: V) => any)): Readonly<V> {
    const rState = this.esMap.get(key);

    if (!rState) {
      this.init(key, typeof value === 'function' ? value(null) : value);
      const initValue = this.esMap.get(key).get();
      this.setStorage(key, initValue);
      return initValue;
    }

    const newValue = rState.set(value as V);
    this.setStorage(key, newValue);
    return newValue;
  }

  /**
   * update the state at key if it exists
   * @param key map key
   * @param value ReactiveState value
   * @returns the updated value or false if update failed
   */
  update(key: K, value: V): Readonly<V> | false;

  /**
   * update the state at key if it exists
   * @param key map key
   * @param immer immer produce function which returns a new state
   * @see immer docs
   * @returns the updated value or false if update failed
   */
  update(key: K, immer: (draft: V) => V): Readonly<V> | false;

  /**
   * update the state at key if it exists
   * @param key map key
   * @param immer immer produce function which mutates the state
   * @see immer docs
   * @returns the updated value or false if update failed
   */
  update(key: K, immer: (draft: V) => void): Readonly<V> | false;

  update(key: K, value: V | ((draft: V) => any)): Readonly<V> | false {
    if (!this.esMap.has(key)) return false;
    return this.set(key, value as V);
  }

  /**
   * get the value at key or optionally initialize it
   * @param key map key
   * @param initValue if state doesn't exist at key initialze it with this value
   */
  get(key: K, initValue?: V): Readonly<V> {
    const state = this.esMap.get(key);
    if (state === undefined) {
      return this.init(key, initValue ?? null);
    }
    return state.get();
  }

  /**
   * get the reactive variable at a given key in the map
   * @param key map key
   * @returns the reactive variable at key or nullVar if it doesn't exist
   */
  getVar(key: K): ReactiveVar<V> {
    if (!this.has(key)) {
      return nullVar;
    }

    return this.esMap.get(key)?.getVar();
  }

  /**
   * iterate over all entries of the map and call a function for each
   * @param callbackfn callback to operate over the entries of the map
   */
  forEach(callbackfn: (value: V, key: K, map: ReactiveMap<K, V>) => void) {
    this.esMap.forEach((value, key) => {
      callbackfn(value.get(), key, this);
    });
  }

  /**
   * set a value for each key in map
   * @param value set this value for each key in map
   */
  setEach(value: V): void;

  /**
   * set a value for each key in map
   * @param immer immer produce function which returns a new state
   * @see immer docs
   */
  setEach(immer: (draft: V) => V): void;

  /**
   * set a value for each key in map
   * @param immer immer produce function which mutates the state
   */
  setEach(immer: (draft: V) => void): void;

  setEach(val: V | ((draft: V) => any)): void {
    if (typeof val === `function`) this.forEach((_v, k) => this.set(k, val));
    else this.forEach((_v, k) => this.set(k, val));
  }

  /**
   * clear storage for the entire map if it exists
   */
  clearStorage() {
    if (this.storageKey)
      graphqlClientStorage.set(`reactive-map-${this.storageKey}`, {});
  }

  /**
   * clear storage and all entries for the map
   * @warn this will cause a cache update
   */
  clear() {
    this.setEach(null);
    this.clearStorage();
    this.esMap.clear();
  }

  /**
   * @returns true if key exists in map
   */
  has = (key: K) => this.esMap.has(key);

  /**
   * @returns true if key was succesfully removed from the map
   */
  delete = (key: K) => {
    this.esMap.delete(key);
    this.deleteStorage(key);
  };

  /**
   * provides an Array.from([this]).map() shortcut
   */
  map = (
    callbackFn: (val: [K, V], index: number, array: readonly [K, V][]) => any
  ) => Array.from(this).map(callbackFn);

  /**
   * provides an Array.from([this]).reduce() shortcut
   */
  reduce = <T extends any>(
    callbackFn: (
      previousValue: T,
      currentValue: readonly [K, V],
      currentIndex: number,
      array: readonly [K, V][]
    ) => T,
    initializer: T
  ) => Array.from(this).reduce(callbackFn, initializer);

  /**
   * provides an Array.from([this]).filter() shortcut
   */
  filter = (
    callbackFn: (
      value: readonly [K, V],
      index: number,
      array: readonly [K, V][]
    ) => boolean
  ) => Array.from(this).filter(callbackFn);
}
